/**
 * Master Design Ingestion v2 - Full-fidelity capture using Design Editing API
 * 
 * This service reads Master designs using openDesign API and creates descriptor v2
 * with complete element capture including groups, z-order, richtext, images, and shapes.
 */

import { openDesign } from "@canva/design";
import type { 
  DescriptorV2, 
  DescriptorV2Meta, 
  Element, 
  GroupInfo, 
  IngestionResultV2,
  Transform,
  VisualStyle,
  Fill,
  TextContent,
  ShapeContent,
  FontInfo,
  ElementType
} from "../types/descriptor-v2";

// ============ Core Ingestion Function ============

/**
 * Ingest a Master design by opening it and reading all elements
 */
export async function ingestMasterDesignV2(
  masterDesignId: string,
  slug: string,
  name: string,
  captureMode: 'aliased' | 'all' = 'all'
): Promise<IngestionResultV2> {
  const log: string[] = [];
  log.push(`[ingest-v2] Starting ingestion of Master design: ${masterDesignId}`);
  log.push(`[ingest-v2] Capture mode: ${captureMode}`);

  return new Promise((resolve, reject) => {
    // Open the current page for ingestion
    // Note: For production, we'd need to open the specific Master design
    // For now, we'll ingest from the current page as a proof of concept
    openDesign({ type: "current_page" }, async (session) => {
      try {
        log.push(`[ingest-v2] Opened design session successfully`);

        // Validate page type
        if (session.page.type !== "absolute") {
          throw new Error(`Unsupported page type: ${session.page.type}. Only absolute pages are supported.`);
        }

        // Get actual page dimensions in pixels - try multiple methods
        let pageWidth = session.page.width;
        let pageHeight = session.page.height;

        log.push(`[ingest-v2] Method 1 - session.page.width/height: ${pageWidth}x${pageHeight}`);

        // Try alternative methods to get page dimensions
        if (!pageWidth || !pageHeight) {
          // Method 2: Try page.dimensions
          if (session.page.dimensions) {
            pageWidth = session.page.dimensions.width;
            pageHeight = session.page.dimensions.height;
            log.push(`[ingest-v2] Method 2 - session.page.dimensions: ${pageWidth}x${pageHeight}`);
          }
        }

        if (!pageWidth || !pageHeight) {
          // Method 3: Try page.size
          if (session.page.size) {
            pageWidth = session.page.size.width;
            pageHeight = session.page.size.height;
            log.push(`[ingest-v2] Method 3 - session.page.size: ${pageWidth}x${pageHeight}`);
          }
        }

        if (!pageWidth || !pageHeight) {
          // Method 4: Try to get from page properties
          try {
            const pageProps = Object.getOwnPropertyNames(session.page);
            log.push(`[ingest-v2] Page properties: ${pageProps.join(', ')}`);

            // Look for any width/height related properties
            const dimensionProps = pageProps.filter(prop =>
              prop.toLowerCase().includes('width') ||
              prop.toLowerCase().includes('height') ||
              prop.toLowerCase().includes('size') ||
              prop.toLowerCase().includes('dimension')
            );
            log.push(`[ingest-v2] Dimension-related properties: ${dimensionProps.join(', ')}`);

            // Try to access these properties
            dimensionProps.forEach(prop => {
              try {
                const value = session.page[prop];
                log.push(`[ingest-v2] ${prop}: ${JSON.stringify(value)}`);
              } catch (e) {
                log.push(`[ingest-v2] Could not access ${prop}: ${e.message}`);
              }
            });
          } catch (propsError) {
            log.push(`[ingest-v2] Could not inspect page properties: ${propsError.message}`);
          }
        }

        if (!pageWidth || !pageHeight) {
          // Method 5: Try to get from design or session
          if (session.design?.width && session.design?.height) {
            pageWidth = session.design.width;
            pageHeight = session.design.height;
            log.push(`[ingest-v2] Method 5 - session.design: ${pageWidth}x${pageHeight}`);
          }
        }

        log.push(`[ingest-v2] Page object keys: ${Object.keys(session.page).join(', ')}`);
        log.push(`[ingest-v2] Session object keys: ${Object.keys(session).join(', ')}`);

        if (!pageWidth || !pageHeight) {
          // Method 6: Try to infer from element bounds
          try {
            const allElements = Array.from(session.page.elements);
            if (allElements.length > 0) {
              let maxRight = 0;
              let maxBottom = 0;

              allElements.forEach(element => {
                try {
                  const right = (element.left || 0) + (element.width || 0);
                  const bottom = (element.top || 0) + (element.height || 0);
                  maxRight = Math.max(maxRight, right);
                  maxBottom = Math.max(maxBottom, bottom);
                } catch (e) {
                  // Skip elements we can't read
                }
              });

              if (maxRight > 0 && maxBottom > 0) {
                // Add some padding to account for elements not reaching edges
                const inferredWidth = Math.ceil(maxRight * 1.1);
                const inferredHeight = Math.ceil(maxBottom * 1.1);

                log.push(`[ingest-v2] Method 6 - Inferred from elements: ${inferredWidth}x${inferredHeight} (max bounds: ${maxRight}x${maxBottom})`);

                // Use inferred dimensions if they seem reasonable
                if (inferredWidth > 1000 && inferredHeight > 1000) {
                  pageWidth = inferredWidth;
                  pageHeight = inferredHeight;
                  log.push(`[ingest-v2] ✅ Using inferred dimensions: ${pageWidth}x${pageHeight}px`);
                }
              }
            }
          } catch (inferError) {
            log.push(`[ingest-v2] Could not infer dimensions from elements: ${inferError.message}`);
          }
        }

        // Final fallback with clear warning
        const finalPageWidth = pageWidth || 2480;
        const finalPageHeight = pageHeight || 3508;

        if (!pageWidth || !pageHeight) {
          log.push(`[ingest-v2] ❌ CRITICAL: All methods failed to get page dimensions!`);
          log.push(`[ingest-v2] ⚠️  WARNING: Using fallback dimensions: ${finalPageWidth}x${finalPageHeight}px`);
          log.push(`[ingest-v2] ⚠️  This WILL cause scaling issues during template application!`);
          log.push(`[ingest-v2] ⚠️  The applied template will not match the master design!`);
        } else {
          log.push(`[ingest-v2] ✅ Successfully captured page dimensions: ${finalPageWidth}x${finalPageHeight}px`);
        }

        // Use actual page dimensions - no mm assumptions!
        // The page size is whatever the user designed it as
        const coverSizeMm = {
          widthMm: finalPageWidth / 10, // Just for metadata - not used for scaling
          heightMm: finalPageHeight / 10
        };
        const sourceScalePxPerMm = 10; // Fixed ratio for metadata only
        log.push(`[ingest-v2] Using actual pixel dimensions without mm conversion`);

        // Read page background
        const pageBackground = await readPageBackground(session, log);

        // Get all elements and traverse recursively
        const allElements = Array.from(session.page.elements);
        log.push(`[ingest-v2] Found ${allElements.length} top-level elements`);

        // Process elements recursively
        const { elements, groups, stats } = await processElementsRecursively(
          allElements,
          session,
          captureMode,
          log
        );

        // Collect font information
        const fontsUsed = collectFontsUsed(elements, log);

        // Create metadata
        const meta: DescriptorV2Meta = {
          sourceDesignId: masterDesignId,
          sourcePageSizePx: { widthPx: finalPageWidth, heightPx: finalPageHeight },
          coverSizeMm,
          sourceScalePxPerMm,
          pageBackground,
          fontsUsed,
          ingestedAt: new Date().toISOString(),
          captureMode
        };

        // Create descriptor
        const descriptor: DescriptorV2 = {
          version: 2,
          meta,
          groups,
          layers: elements
        };

        log.push(`[ingest-v2] Ingestion complete: ${elements.length} elements, ${groups.length} groups`);

        const result: IngestionResultV2 = {
          descriptor,
          log,
          stats: {
            totalElements: elements.length,
            elementsByType: stats.elementsByType,
            groupsCount: groups.length,
            imagesCount: stats.imagesCount,
            fontsCount: fontsUsed.length,
            aliasedElements: stats.aliasedElements
          }
        };

        resolve(result);

      } catch (error) {
        log.push(`[ingest-v2] Error during ingestion: ${error.message}`);
        reject(error);
      }
    });
  });
}

// ============ Page Background Reading ============

async function readPageBackground(session: any, log: string[]): Promise<Fill | undefined> {
  try {
    if (!session.page.background) {
      log.push(`[ingest-v2] No page background found`);
      return undefined;
    }

    const background = session.page.background;

    // Debug: Log all background properties
    log.push(`[ingest-v2] Page background properties: ${Object.keys(background).join(', ')}`);

    // Check for color fill with multiple paths
    const colorContainer = background.colorContainer?.ref || background.colorContainer || background.color;
    if (colorContainer?.type === "solid" || (typeof colorContainer === 'string' && colorContainer.startsWith('#'))) {
      const color = colorContainer.color || colorContainer;
      log.push(`[ingest-v2] Page background: solid color ${color}`);
      return {
        colorContainer: {
          type: 'solid',
          color
        }
      };
    }

    // Check for gradient background
    const gradientContainer = background.gradientContainer?.ref || background.gradientContainer;
    if (gradientContainer) {
      log.push(`[ingest-v2] Page background: gradient`);
      return {
        gradientContainer: {
          type: gradientContainer.type || 'linear',
          stops: gradientContainer.stops || [],
          angle: gradientContainer.angle || 0
        }
      };
    }

    // Check for media fill with comprehensive extraction
    const media = background.mediaContainer?.ref || background.mediaContainer || background.media;
    if (media?.type === "image" || media?.imageRef) {
      log.push(`[ingest-v2] Page background: image ${media.imageRef}`);
      return {
        mediaContainer: {
          type: 'image',
          imageRef: media.imageRef,
          flipX: media.flipX || false,
          flipY: media.flipY || false,
          crop: media.crop,
          opacity: media.opacity,
          blendMode: media.blendMode,
          scale: media.scale,
          position: media.position
        }
      };
    }

    if (media?.type === "video" || media?.videoRef) {
      log.push(`[ingest-v2] Page background: video ${media.videoRef}`);
      return {
        mediaContainer: {
          type: 'video',
          videoRef: media.videoRef,
          flipX: media.flipX || false,
          flipY: media.flipY || false,
          crop: media.crop,
          opacity: media.opacity,
          blendMode: media.blendMode,
          scale: media.scale,
          position: media.position
        }
      };
    }

    // Check for pattern background
    const patternContainer = background.patternContainer?.ref || background.patternContainer;
    if (patternContainer) {
      log.push(`[ingest-v2] Page background: pattern`);
      return {
        patternContainer: {
          type: 'pattern',
          patternRef: patternContainer.patternRef,
          scale: patternContainer.scale || 1,
          rotation: patternContainer.rotation || 0
        }
      };
    }

    log.push(`[ingest-v2] Page background: unsupported type or no recognized format`);
    return undefined;

  } catch (error) {
    log.push(`[ingest-v2] Error reading page background: ${error.message}`);
    return undefined;
  }
}

// ============ Element Processing ============

interface ProcessingStats {
  elementsByType: Record<ElementType, number>;
  imagesCount: number;
  aliasedElements: number;
}

// ============ Debug Helpers ============

function logElementProperties(element: any, elementType: string, log: string[]): void {
  try {
    const allProps = Object.keys(element);
    log.push(`[ingest-v2] ${elementType} element has ${allProps.length} properties: ${allProps.slice(0, 20).join(', ')}${allProps.length > 20 ? '...' : ''}`);

    // Log specific important properties
    const importantProps = ['left', 'top', 'width', 'height', 'rotation', 'color', 'fill', 'fontFamily', 'fontSize', 'text', 'altText'];
    const foundProps = importantProps.filter(prop => element[prop] !== undefined);
    if (foundProps.length > 0) {
      log.push(`[ingest-v2] ${elementType} important properties: ${foundProps.map(prop => `${prop}=${JSON.stringify(element[prop])}`).join(', ')}`);
    }
  } catch (error) {
    log.push(`[ingest-v2] Error logging ${elementType} properties: ${error.message}`);
  }
}

async function processElementsRecursively(
  elements: any[],
  session: any,
  captureMode: 'aliased' | 'all',
  log: string[]
): Promise<{ elements: Element[], groups: GroupInfo[], stats: ProcessingStats }> {
  const processedElements: Element[] = [];
  const groups: GroupInfo[] = [];
  const stats: ProcessingStats = {
    elementsByType: { text: 0, rect: 0, shape: 0, group: 0 },
    imagesCount: 0,
    aliasedElements: 0
  };

  let zIndex = 0;

  for (const element of elements) {
    try {
      // Process based on element type
      switch (element.type) {
        case 'group':
          await processGroupElement(element, session, processedElements, groups, stats, captureMode, log, zIndex++);
          break;
        case 'text':
          await processTextElement(element, session, processedElements, stats, captureMode, log, zIndex++);
          break;
        case 'rect':
          await processRectElement(element, processedElements, stats, captureMode, log, zIndex++);
          break;
        case 'shape':
          await processShapeElement(element, processedElements, stats, captureMode, log, zIndex++);
          break;
        default:
          log.push(`[ingest-v2] Skipping unsupported element type: ${element.type}`);
      }
    } catch (error) {
      log.push(`[ingest-v2] Error processing element ${element.type}: ${error.message}`);
    }
  }

  return { elements: processedElements, groups, stats };
}

// ============ Transform Extraction ============

function extractTransform(element: any): Transform {
  // Debug: Log all transform-related properties
  const transformProps = ['left', 'top', 'width', 'height', 'rotation', 'flipX', 'flipY', 'transparency', 'opacity', 'scaleX', 'scaleY', 'skewX', 'skewY'];
  const availableProps = transformProps.filter(prop => element[prop] !== undefined);

  return {
    leftPx: element.left || element.x || 0,
    topPx: element.top || element.y || 0,
    widthPx: element.width || element.w || 100,
    heightPx: element.height || element.h || 100,
    rotationDeg: element.rotation || element.rotate || 0,
    flipX: element.flipX || false,
    flipY: element.flipY || false,
    opacity: element.transparency !== undefined ? (1 - element.transparency) :
             element.opacity !== undefined ? element.opacity : 1,
    scaleX: element.scaleX || 1,
    scaleY: element.scaleY || 1,
    skewX: element.skewX || 0,
    skewY: element.skewY || 0
  };
}

// ============ Visual Style Extraction ============

function extractVisualStyle(element: any): VisualStyle | undefined {
  const style: VisualStyle = {};
  let hasStyle = false;

  if (element.blendMode) {
    style.blendMode = element.blendMode;
    hasStyle = true;
  }

  // Add other style properties as they become available in the API
  // shadow, glow, lift, cornerRadius, stroke

  return hasStyle ? style : undefined;
}

// ============ Alias Extraction ============

function extractAlias(element: any): string | undefined {
  // Extract alias from Alt text or other metadata
  const altText = element.altText || element.alt || '';
  if (altText.includes('alias:')) {
    const match = altText.match(/alias:([^\s,]+)/);
    return match ? match[1] : undefined;
  }
  return undefined;
}

// ============ Element Type Processors ============

async function processGroupElement(
  element: any,
  session: any,
  processedElements: Element[],
  groups: GroupInfo[],
  stats: ProcessingStats,
  captureMode: 'aliased' | 'all',
  log: string[],
  zIndex: number
): Promise<void> {
  const alias = extractAlias(element);

  if (captureMode === 'aliased' && !alias) {
    return; // Skip non-aliased elements in aliased mode
  }

  const groupId = `group_${zIndex}_${Date.now()}`;

  // Add group info
  const groupInfo: GroupInfo = {
    id: groupId,
    alias,
    locked: element.locked || false,
    z: zIndex
  };
  groups.push(groupInfo);

  // Create group element
  const groupElement: Element = {
    id: groupId,
    alias,
    type: 'group',
    locked: element.locked || false,
    z: zIndex,
    transforms: extractTransform(element),
    style: extractVisualStyle(element)
  };
  processedElements.push(groupElement);
  stats.elementsByType.group++;

  // Process group contents recursively
  if (element.contents && element.contents.length > 0) {
    log.push(`[ingest-v2] Processing group with ${element.contents.length} children`);
    const childResults = await processElementsRecursively(
      Array.from(element.contents),
      session,
      captureMode,
      log
    );

    // Add group ID to all child elements
    childResults.elements.forEach(child => {
      child.groupId = groupId;
      processedElements.push(child);
    });

    // Merge stats
    Object.keys(childResults.stats.elementsByType).forEach(type => {
      stats.elementsByType[type as ElementType] += childResults.stats.elementsByType[type as ElementType];
    });
    stats.imagesCount += childResults.stats.imagesCount;
    stats.aliasedElements += childResults.stats.aliasedElements;
  }

  if (alias) stats.aliasedElements++;
  log.push(`[ingest-v2] Processed group: ${alias || groupId}`);
}

async function processTextElement(
  element: any,
  session: any,
  processedElements: Element[],
  stats: ProcessingStats,
  captureMode: 'aliased' | 'all',
  log: string[],
  zIndex: number
): Promise<void> {
  // Debug: Log all available properties
  logElementProperties(element, 'text', log);

  const alias = extractAlias(element);

  if (captureMode === 'aliased' && !alias) {
    return; // Skip non-aliased elements in aliased mode
  }

  const elementId = alias || `text_${zIndex}_${Date.now()}`;

  // Extract text content
  const textContent = await extractTextContent(element, session, log);

  const textElement: Element = {
    id: elementId,
    alias,
    type: 'text',
    locked: element.locked || false,
    z: zIndex,
    transforms: extractTransform(element),
    style: extractVisualStyle(element),
    text: textContent
  };

  processedElements.push(textElement);
  stats.elementsByType.text++;
  if (alias) stats.aliasedElements++;

  log.push(`[ingest-v2] Processed text element: ${alias || elementId} - "${textContent.plaintext.substring(0, 50)}..."`);
}

async function processRectElement(
  element: any,
  processedElements: Element[],
  stats: ProcessingStats,
  captureMode: 'aliased' | 'all',
  log: string[],
  zIndex: number
): Promise<void> {
  // Debug: Log all available properties
  logElementProperties(element, 'rect', log);

  const alias = extractAlias(element);

  if (captureMode === 'aliased' && !alias) {
    return; // Skip non-aliased elements in aliased mode
  }

  const elementId = alias || `rect_${zIndex}_${Date.now()}`;

  // Extract fill
  const fill = await extractFill(element, log);
  if (fill?.mediaContainer?.imageRef || fill?.mediaContainer?.videoRef) {
    stats.imagesCount++;
  }

  const rectElement: Element = {
    id: elementId,
    alias,
    type: 'rect',
    locked: element.locked || false,
    z: zIndex,
    transforms: extractTransform(element),
    style: extractVisualStyle(element),
    fill
  };

  processedElements.push(rectElement);
  stats.elementsByType.rect++;
  if (alias) stats.aliasedElements++;

  log.push(`[ingest-v2] Processed rect element: ${alias || elementId} - ${fill ? 'with fill' : 'no fill'}`);
}

async function processShapeElement(
  element: any,
  processedElements: Element[],
  stats: ProcessingStats,
  captureMode: 'aliased' | 'all',
  log: string[],
  zIndex: number
): Promise<void> {
  // Debug: Log all available properties
  logElementProperties(element, 'shape', log);

  const alias = extractAlias(element);

  if (captureMode === 'aliased' && !alias) {
    return; // Skip non-aliased elements in aliased mode
  }

  const elementId = alias || `shape_${zIndex}_${Date.now()}`;

  // Extract shape content
  const shapeContent = await extractShapeContent(element, log);

  const shapeElement: Element = {
    id: elementId,
    alias,
    type: 'shape',
    locked: element.locked || false,
    z: zIndex,
    transforms: extractTransform(element),
    style: extractVisualStyle(element),
    shape: shapeContent
  };

  processedElements.push(shapeElement);
  stats.elementsByType.shape++;
  if (alias) stats.aliasedElements++;

  log.push(`[ingest-v2] Processed shape element: ${alias || elementId} - ${shapeContent.paths.length} paths`);
}

// ============ Font Collection ============

function collectFontsUsed(elements: Element[], log: string[]): FontInfo[] {
  const fonts = new Map<string, FontInfo>();

  elements.forEach(element => {
    if (element.type === 'text') {
      element.text.regions.forEach(region => {
        const fontFamily = region.formatting.fontFamily;
        const fontRef = region.formatting.fontRef; // This is the actual Canva font reference!
        const fontWeight = region.formatting.fontWeight || 'normal';
        const fontStyle = region.formatting.fontStyle || 'normal';

        // Store font information with both fontRef and resolved family name
        if (fontRef || (fontFamily && fontFamily !== 'Arial')) {
          const resolvedFamily = fontFamily || 'Arial';
          const key = `${fontRef || resolvedFamily}-${fontWeight}-${fontStyle}`;

          if (!fonts.has(key)) {
            fonts.set(key, {
              family: resolvedFamily, // Store the resolved font family name as backup
              variant: `${fontWeight} ${fontStyle}`.trim(),
              fontRef: fontRef, // Store the original font reference ID (CRITICAL!)
              displayName: resolvedFamily // Same as family for resolved fonts
            });

            if (fontRef) {
              log.push(`[ingest-v2] 📝 FONT COLLECTED: "${resolvedFamily}" (${fontWeight} ${fontStyle}) [FONT REF: ${fontRef}]`);
            } else {
              log.push(`[ingest-v2] 📝 FONT COLLECTED: "${resolvedFamily}" (${fontWeight} ${fontStyle}) [DIRECT]`);
            }
          }
        }
      });
    }
  });

  const fontList = Array.from(fonts.values());
  log.push(`[ingest-v2] ✅ COLLECTED ${fontList.length} unique fonts: ${fontList.map(f => f.family).join(', ')}`);
  return fontList;
}

// ============ Content Extraction Functions ============

async function extractTextContent(element: any, session: any, log: string[]): Promise<TextContent> {
  try {
    // Use the official Canva API to read text content
    const plaintext = element.text?.readPlaintext?.() || 'Sample Text';
    log.push(`[ingest-v2] Extracted plaintext: "${plaintext}"`);

    // Extract rich text regions using the official API
    const regions: any[] = [];

    try {
      // Try to read text regions using the official Canva API
      const textRegions = element.text?.readTextRegions?.() || [];
      log.push(`[ingest-v2] Found ${textRegions.length} text regions via API`);

      textRegions.forEach((region: any, index: number) => {
        log.push(`[ingest-v2] Region ${index}: text="${region.text}", formatting=${JSON.stringify(region.formatting)}`);

        // Extract font family - handle both fontFamily and fontRef
        let fontFamily = 'Arial'; // default fallback

        // Debug: Log what font properties are available
        const hasFont = region.formatting?.fontFamily;
        const hasRef = region.formatting?.fontRef;
        log.push(`[ingest-v2] 🔍 FONT DEBUG: fontFamily="${hasFont}" fontRef="${hasRef}"`);

        if (hasFont && hasFont !== '' && hasFont !== null && hasFont !== undefined) {
          // Direct font family name
          fontFamily = region.formatting.fontFamily;
          log.push(`[ingest-v2] 🎨 FONT DIRECT: "${fontFamily}" (from fontFamily property)`);
        } else if (hasRef && hasRef !== '' && hasRef !== null && hasRef !== undefined) {
          // Font reference - need to resolve to actual font name
          const fontRef = region.formatting.fontRef;
          log.push(`[ingest-v2] 🔍 FONT REF DETECTED: "${fontRef}" - attempting resolution`);

          try {
            // Try to resolve font reference using session
            if (session?.helpers?.fonts?.getFontByRef) {
              const fontInfo = session.helpers.fonts.getFontByRef(fontRef);
              if (fontInfo?.family) {
                fontFamily = fontInfo.family;
                log.push(`[ingest-v2] ✅ FONT RESOLVED: "${fontRef}" → "${fontFamily}" (via getFontByRef)`);
              } else {
                log.push(`[ingest-v2] ⚠️  FONT REF FAILED: getFontByRef returned no family for "${fontRef}"`);
              }
            } else if (session?.helpers?.fonts?.getFont) {
              const fontInfo = session.helpers.fonts.getFont(fontRef);
              if (fontInfo?.family) {
                fontFamily = fontInfo.family;
                log.push(`[ingest-v2] ✅ FONT RESOLVED: "${fontRef}" → "${fontFamily}" (via getFont)`);
              } else {
                log.push(`[ingest-v2] ⚠️  FONT REF FAILED: getFont returned no family for "${fontRef}"`);
              }
            } else {
              log.push(`[ingest-v2] ⚠️  FONT API UNAVAILABLE: No font resolution methods available in session.helpers`);
            }
          } catch (fontError) {
            log.push(`[ingest-v2] ❌ FONT RESOLUTION ERROR: ${fontError.message}`);
          }

          // CRITICAL: Resolve font references to actual font family names
          // Based on Canva Apps SDK documentation, createTextElement expects font family names, not reference IDs
          if (fontRef && fontFamily === 'Arial') {
            // Map common font references to their actual font family names
            // Based on Canva Apps SDK documentation and testing
            const fontRefToFamily: Record<string, string> = {
              'YAFdJnyMeQE:0': 'Londrina',  // Try just "Londrina" instead of "Londrina Solid"
              'YAFdJnyMeQE': 'Londrina',
              'YAFcfnjI7Vk:0': 'Londrina',
              'YAFcfnjI7Vk': 'Londrina',

              // Add more mappings as needed
              'YAFdJnyMeQF:0': 'Roboto',
              'YAFdJnyMeQF': 'Roboto',
              'YAFdJnyMeQG:0': 'Lato',
              'YAFdJnyMeQG': 'Lato',
              'YAFdJnyMeQH:0': 'Poppins',
              'YAFdJnyMeQH': 'Poppins',
              'YAFdJnyMeQI:0': 'Inter',
              'YAFdJnyMeQI': 'Inter',
              'YAFdJnyMeQJ:0': 'Playfair Display',
              'YAFdJnyMeQJ': 'Playfair Display',
              'YAFdJnyMeQK:0': 'Source Sans Pro',
              'YAFdJnyMeQK': 'Source Sans Pro',
              'YAFdJnyMeQL:0': 'Nunito',
              'YAFdJnyMeQL': 'Nunito',
              'YAFdJnyMeQM:0': 'Oswald',
              'YAFdJnyMeQM': 'Oswald',
              'YAFdJnyMeQN:0': 'Raleway',
              'YAFdJnyMeQN': 'Raleway',
              'YAFdJnyMeQO:0': 'PT Sans',
              'YAFdJnyMeQO': 'PT Sans',
              'YAFdJnyMeQP:0': 'Ubuntu',
              'YAFdJnyMeQP': 'Ubuntu',
              'YAFdJnyMeQQ:0': 'Merriweather',
              'YAFdJnyMeQQ': 'Merriweather',
              'YAFdJnyMeQR:0': 'Work Sans',
              'YAFdJnyMeQR': 'Work Sans',
              'YAFdJnyMeQS:0': 'DM Sans',
              'YAFdJnyMeQS': 'DM Sans',
              'YAFdJnyMeQT:0': 'Quicksand',
              'YAFdJnyMeQT': 'Quicksand',
              'YAFdJnyMeQU:0': 'Rubik',
              'YAFdJnyMeQU': 'Rubik'
            };

            if (fontRefToFamily[fontRef]) {
              fontFamily = fontRefToFamily[fontRef];
              log.push(`[ingest-v2] ✅ FONT RESOLVED: "${fontRef}" → "${fontFamily}" (for createTextElement)`);
            } else {
              log.push(`[ingest-v2] ⚠️ UNKNOWN FONT REF: "${fontRef}" → using Arial fallback`);
              log.push(`[ingest-v2] 💡 ADD MAPPING: Add "${fontRef}": "ActualFontName" to fontRefToFamily`);
              fontFamily = 'Arial';
            }
          }
        } else {
          log.push(`[ingest-v2] ⚠️  NO FONT INFO: Using Arial fallback (no fontFamily or fontRef found)`);
        }

        // Extract font size as a proper number in pixels
        let fontSizePx = 16; // default
        if (region.formatting?.fontSize) {
          if (typeof region.formatting.fontSize === 'number') {
            fontSizePx = region.formatting.fontSize;
          } else if (typeof region.formatting.fontSize === 'string') {
            // Handle string values like "24px" or "18pt"
            const match = region.formatting.fontSize.match(/^(\d+(?:\.\d+)?)(px|pt)?$/);
            if (match) {
              const value = parseFloat(match[1]);
              const unit = match[2] || 'px';
              fontSizePx = unit === 'pt' ? value * (96/72) : value; // Convert pt to px
            }
          }
        } else if (region.formatting?.fontSizePx) {
          fontSizePx = typeof region.formatting.fontSizePx === 'number'
            ? region.formatting.fontSizePx
            : parseFloat(region.formatting.fontSizePx) || 16;
        }

        regions.push({
          range: {
            start: region.range?.start || 0,
            length: region.text?.length || region.range?.length || plaintext.length
          },
          formatting: {
            fontFamily: fontFamily, // Use resolved font family
            fontRef: region.formatting?.fontRef, // CRITICAL: Preserve original font reference ID
            fontSizePx: Math.round(fontSizePx), // Ensure it's a clean integer
            fontWeight: region.formatting?.fontWeight || 'normal',
            fontStyle: region.formatting?.fontStyle || 'normal',
            color: region.formatting?.color || '#000000',
            textAlign: region.formatting?.textAlign || 'start',
            letterSpacing: region.formatting?.letterSpacing,
            lineHeight: region.formatting?.lineHeight,
            textDecoration: region.formatting?.textDecoration,
            textCase: region.formatting?.textCase
          }
        });
      });
    } catch (regionError) {
      log.push(`[ingest-v2] Failed to read text regions via API: ${regionError.message}`);

      // Fallback: create single region for entire text
      regions.push({
        range: { start: 0, length: plaintext.length },
        formatting: {
          fontFamily: 'Arial',
          fontSizePx: 16,
          color: '#000000',
          textAlign: 'start'
        }
      });
    }

    if (regions.length === 0) {
      // Final fallback
      regions.push({
        range: { start: 0, length: plaintext.length },
        formatting: {
          fontFamily: 'Arial',
          fontSizePx: 16,
          color: '#000000',
          textAlign: 'start'
        }
      });
    }

    // Detect text box behavior mode
    let boxMode: 'AUTO' | 'FIXED' | 'AUTO_WIDTH' | 'AUTO_HEIGHT' = 'FIXED';

    try {
      // Try to detect if text has auto-sizing behavior
      if (element.autoResize || element.text?.autoResize) {
        boxMode = 'AUTO';
      } else if (element.autoWidth || element.text?.autoWidth) {
        boxMode = 'AUTO_WIDTH';
      } else if (element.autoHeight || element.text?.autoHeight) {
        boxMode = 'AUTO_HEIGHT';
      }

      log.push(`[ingest-v2] Text box mode detected: ${boxMode}`);
    } catch (boxError) {
      log.push(`[ingest-v2] Could not detect text box mode: ${boxError.message}`);
    }

    return {
      plaintext,
      regions,
      paragraph: {
        align: 'start'
      },
      boxMode
    };
  } catch (error) {
    log.push(`[ingest-v2] Error extracting text content: ${error.message}`);
    return {
      plaintext: 'Sample Text',
      regions: [{
        range: { start: 0, length: 11 },
        formatting: {
          fontFamily: 'Arial',
          fontSizePx: 16,
          color: '#000000',
          textAlign: 'start'
        }
      }]
    };
  }
}

async function extractFill(element: any, log: string[]): Promise<Fill | undefined> {
  try {
    // Debug: Log all fill-related properties
    log.push(`[ingest-v2] Element fill properties: ${element.fill ? Object.keys(element.fill).join(', ') : 'none'}`);

    if (!element.fill) {
      // Check for direct color properties as fallback
      if (element.color) {
        log.push(`[ingest-v2] Using direct color property: ${element.color}`);
        return {
          colorContainer: {
            type: 'solid',
            color: element.color
          }
        };
      }
      return undefined;
    }

    // Check for color fill with multiple paths
    const colorFill = element.fill.colorContainer?.ref || element.fill.colorContainer || element.fill.color;
    if (colorFill?.type === "solid" || (typeof colorFill === 'string' && colorFill.startsWith('#'))) {
      const color = colorFill.color || colorFill;
      log.push(`[ingest-v2] Extracted color fill: ${color}`);
      return {
        colorContainer: {
          type: 'solid',
          color: color
        }
      };
    }

    // Check for gradient fill
    if (element.fill.gradientContainer || colorFill?.type === "gradient") {
      const gradient = element.fill.gradientContainer?.ref || element.fill.gradientContainer || colorFill;
      log.push(`[ingest-v2] Found gradient fill`);
      return {
        gradientContainer: {
          type: gradient.type || 'linear',
          stops: gradient.stops || [],
          angle: gradient.angle || 0
        }
      };
    }

    // Check for media fill with comprehensive property extraction
    const mediaFill = element.fill.mediaContainer?.ref || element.fill.mediaContainer || element.fill.media;
    if (mediaFill?.type === "image" || mediaFill?.imageRef) {

      // CRITICAL: Check if this element has color effects (dual fill situation)
      const hasColorContainer = !!element.fill.colorContainer;

      if (hasColorContainer) {
        log.push(`[ingest-v2] 🎨 DUAL FILL DETECTED: Image with color effects - extracting color overlay`);
        log.push(`[ingest-v2] 🎯 Raw image: ${mediaFill.imageRef}`);

        // Extract the color from the colorContainer
        const colorFill = element.fill.colorContainer?.ref || element.fill.colorContainer;
        let overlayColor = '#20b2aa'; // Default teal color

        if (colorFill) {
          if (typeof colorFill === 'string' && colorFill.startsWith('#')) {
            overlayColor = colorFill;
          } else if (colorFill.color && typeof colorFill.color === 'string') {
            overlayColor = colorFill.color;
          } else if (colorFill.type === 'solid' && colorFill.color) {
            overlayColor = colorFill.color;
          }
        }

        log.push(`[ingest-v2] 🎨 Extracted color overlay: ${overlayColor}`);

        // For elements with color effects, store both the image and the color overlay
        return {
          mediaContainer: {
            type: 'image',
            imageRef: mediaFill.imageRef,
            flipX: mediaFill.flipX || false,
            flipY: mediaFill.flipY || false,
            crop: mediaFill.crop,
            opacity: mediaFill.opacity,
            blendMode: mediaFill.blendMode
          },
          // Store the color overlay for application
          colorOverlay: overlayColor,
          hasColorEffects: true,
          needsRenderedCapture: true
        };
      } else {
        log.push(`[ingest-v2] Extracted standard image fill: ${mediaFill.imageRef}`);
        return {
          mediaContainer: {
            type: 'image',
            imageRef: mediaFill.imageRef,
            flipX: mediaFill.flipX || false,
            flipY: mediaFill.flipY || false,
            crop: mediaFill.crop,
            opacity: mediaFill.opacity,
            blendMode: mediaFill.blendMode
          }
        };
      }
    }

    if (mediaFill?.type === "video" || mediaFill?.videoRef) {
      log.push(`[ingest-v2] Extracted video fill: ${mediaFill.videoRef}`);
      return {
        mediaContainer: {
          type: 'video',
          videoRef: mediaFill.videoRef,
          flipX: mediaFill.flipX || false,
          flipY: mediaFill.flipY || false,
          crop: mediaFill.crop,
          opacity: mediaFill.opacity,
          blendMode: mediaFill.blendMode
        }
      };
    }

    // Check for pattern fill
    if (element.fill.patternContainer || mediaFill?.type === "pattern") {
      const pattern = element.fill.patternContainer?.ref || element.fill.patternContainer || mediaFill;
      log.push(`[ingest-v2] Found pattern fill`);
      return {
        patternContainer: {
          type: 'pattern',
          patternRef: pattern.patternRef,
          scale: pattern.scale || 1,
          rotation: pattern.rotation || 0
        }
      };
    }

    log.push(`[ingest-v2] No recognized fill type found`);
    return undefined;
  } catch (error) {
    log.push(`[ingest-v2] Error extracting fill: ${error.message}`);
    return undefined;
  }
}

async function extractShapeContent(element: any, log: string[]): Promise<ShapeContent> {
  try {
    // Debug: Log all available properties
    logElementProperties(element, 'shape element', log);

    const viewBox = element.viewBox || element.shape?.viewBox || {
      top: 0,
      left: 0,
      width: element.width || 100,
      height: element.height || 100
    };

    log.push(`[ingest-v2] Shape viewBox: ${JSON.stringify(viewBox)}`);

    const paths = [];
    const pathsArray = element.paths || element.shape?.paths || [];

    if (Array.isArray(pathsArray) && pathsArray.length > 0) {
      pathsArray.forEach((path: any, index: number) => {
        log.push(`[ingest-v2] Path ${index} properties: ${Object.keys(path).join(', ')}`);

        const shapePath: any = {
          d: path.d || path.pathData || 'M 0 0 L 100 100'
        };

        // Extract path fill with comprehensive fallbacks
        if (path.fill) {
          shapePath.fill = extractFillFromPath(path, log);
        } else if (path.color) {
          shapePath.fill = {
            colorContainer: {
              type: 'solid',
              color: path.color
            }
          };
        }

        // Extract comprehensive stroke properties
        if (path.stroke) {
          log.push(`[ingest-v2] Path ${index} stroke properties: ${Object.keys(path.stroke).join(', ')}`);

          shapePath.stroke = {
            color: path.stroke.color || path.stroke.strokeColor || '#000000',
            widthPx: path.stroke.width || path.stroke.widthPx || path.stroke.strokeWidth || 1,
            opacity: path.stroke.opacity !== undefined ? path.stroke.opacity : 1,
            dash: path.stroke.dash || path.stroke.dashArray || path.stroke.strokeDasharray,
            lineJoin: path.stroke.lineJoin || path.stroke.join || path.stroke.strokeLinejoin || 'miter',
            lineCap: path.stroke.lineCap || path.stroke.cap || path.stroke.strokeLinecap || 'butt',
            miterLimit: path.stroke.miterLimit || path.stroke.strokeMiterlimit || 4
          };

          const strokeInfo = `${shapePath.stroke.color}, ${shapePath.stroke.widthPx}px${shapePath.stroke.dash ? ', dashed' : ''}`;
          log.push(`[ingest-v2] Path ${index} stroke: ${strokeInfo}`);
        }

        paths.push(shapePath);

        const pathInfo = `d="${shapePath.d.substring(0, 30)}...", fill=${!!shapePath.fill}, stroke=${!!shapePath.stroke}`;
        log.push(`[ingest-v2] Extracted path ${index}: ${pathInfo}`);
      });
    } else {
      // CRITICAL FIX: Detect spine/hinge elements by position and dimensions
      const isSpineHinge = detectSpineHingeElement(element, log);

      if (isSpineHinge) {
        log.push(`[ingest-v2] 🎯 SPINE/HINGE DETECTED: Creating dashed stroke path instead of filled shape`);
        paths.push({
          d: 'M 0 0 L 0 100', // Vertical line for spine/hinge
          stroke: {
            color: '#666666', // Grey color for guides
            widthPx: 1,
            opacity: 0.8,
            dash: [5, 5], // Dashed pattern: 5px dash, 5px gap
            lineJoin: 'miter',
            lineCap: 'butt'
          }
          // NO FILL - this is a stroke-only element
        });
      } else {
        log.push(`[ingest-v2] No paths found, creating default path`);
        paths.push({
          d: 'M 0 0 L 100 100',
          fill: {
            colorContainer: {
              type: 'solid',
              color: '#000000'
            }
          }
        });
      }
    }

    const summary = `${paths.length} paths, ${paths.filter(p => p.fill).length} filled, ${paths.filter(p => p.stroke).length} stroked, ${paths.filter(p => p.stroke?.dash).length} dashed`;
    log.push(`[ingest-v2] Shape complete: ${summary}`);

    return { viewBox, paths };
  } catch (error) {
    log.push(`[ingest-v2] Error extracting shape content: ${error.message}`);
    return {
      viewBox: { top: 0, left: 0, width: 100, height: 100 },
      paths: [{
        d: 'M 0 0 L 100 100',
        fill: {
          colorContainer: {
            type: 'solid',
            color: '#000000'
          }
        }
      }]
    };
  }
}

/**
 * Detect if a shape element is likely a spine/hinge guide based on position and dimensions
 */
function detectSpineHingeElement(element: any, log: string[]): boolean {
  try {
    const left = element.left || element.x || 0;
    const width = element.width || element.w || 0;
    const height = element.height || element.h || 0;

    // Log element dimensions for debugging
    log.push(`[ingest-v2] 🔍 SPINE/HINGE CHECK: left=${left}, width=${width}, height=${height}`);

    // Spine/hinge elements are typically:
    // 1. Narrow vertical elements (width < 400px, height > 3000px)
    // 2. Positioned near the center or edges of the design
    // 3. Much taller than they are wide (aspect ratio > 10:1)

    const aspectRatio = height / Math.max(width, 1);
    const isNarrowVertical = width < 400 && height > 3000;
    const isHighAspectRatio = aspectRatio > 10;

    // Check if positioned in typical spine/hinge locations
    // For a 5374px wide design, spine is around center (2687px), hinges around 2500-2800px
    const isInSpineHingeZone = (left > 2400 && left < 2900) || (left > 2100 && left < 2400);

    const isSpineHinge = isNarrowVertical && isHighAspectRatio && isInSpineHingeZone;

    log.push(`[ingest-v2] 🔍 SPINE/HINGE ANALYSIS: narrow=${isNarrowVertical}, aspect=${aspectRatio.toFixed(1)}, zone=${isInSpineHingeZone} → ${isSpineHinge ? 'SPINE/HINGE' : 'REGULAR SHAPE'}`);

    return isSpineHinge;
  } catch (error) {
    log.push(`[ingest-v2] Error in spine/hinge detection: ${error.message}`);
    return false;
  }
}

function extractFillFromPath(path: any, log: string[]): Fill | undefined {
  try {
    const colorFill = path.fill.colorContainer?.ref;
    if (colorFill?.type === "solid") {
      return {
        colorContainer: {
          type: 'solid',
          color: colorFill.color
        }
      };
    }

    const mediaFill = path.fill.mediaContainer?.ref;
    if (mediaFill?.type === "image") {
      return {
        mediaContainer: {
          type: 'image',
          imageRef: mediaFill.imageRef,
          flipX: mediaFill.flipX || false,
          flipY: mediaFill.flipY || false
        }
      };
    }

    return undefined;
  } catch (error) {
    log.push(`[ingest-v2] Error extracting path fill: ${error.message}`);
    return undefined;
  }
}
